import {
  Box,
  DialogContent,
  Divider,
  IconButton,
  Typography,
  Stack,
  RadioGroup,
  Radio,
  FormControl,
  FormLabel,
  Button,
  styled,
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import {type ModalDialogProps} from 'interfaces';
import CloseIcon from '@mui/icons-material/Close';
import {useState, useEffect, type FC, type ChangeEvent, type SetStateAction, type Dispatch} from 'react';
import {useGetPlan} from 'hooks/swr';
import Alert from 'helpers/Alert';

import FormGroup from '@mui/material/FormGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';

import useGetLongTermRange from 'hooks/useGetLongTermRange';
import GetApp from '@mui/icons-material/GetApp';
import useParseURL from 'hooks/useParseURL';
import FileDownload from 'js-file-download';
import GenericDialog from 'components/Generic/GenericDialog';
import axios, {type AxiosResponse} from 'axios';
import {type Plan} from 'client';
import {dateReader, localDateSender} from 'helpers/UnitsFormatHandler';
import backendCommunicationInstance from 'services/backendCommunicator';
import {useTranslation} from 'react-i18next';
import {getBaseUrl} from '../../utils/runtimeVars';
import {InputMenuDatePicker} from 'components/Generic/DatePickersV2/InputMenuDatePicker';

const fileFields = {
  openCount: {columnName: 'Open Count', selected: true},
  openWeight: {columnName: 'Open Weight', selected: true},
  openBiomass: {columnName: 'Open Biomass', selected: true},
  closingCount: {columnName: 'Closing Count', selected: true},
  closingWeight: {columnName: 'Closing Weight', selected: true},
  closingBiomass: {columnName: 'Closing Biomass', selected: true},
  fishTypeName: {columnName: 'Fish Type', selected: true},
  mortalityCount: {columnName: 'Mortality Count', selected: true},
  growthRate: {columnName: 'Growth Rate', selected: true},
  absGrowth: {columnName: 'Absolute Growth', selected: true},
  liceCount: {columnName: 'Lice Count', selected: true},
  harvestCount: {columnName: 'Harvest Count', selected: true},
  harvestWeight: {columnName: 'Harvest Weight (LWE)', selected: true},
  harvestBiomassLWE: {columnName: 'Harvest Biomass (LWE)', selected: true},
  inputCount: {columnName: 'Input Count', selected: true},
  inputWeight: {columnName: 'Input Weight', selected: true},
  inputBiomass: {columnName: 'Input Biomass', selected: true},
  transferCount: {columnName: 'Transfer In Count', selected: true},
  transferInWeight: {columnName: 'Transfer In Weight', selected: true},
  transferInBiomass: {columnName: 'Transfer In Biomass', selected: true},
  transferOutCount: {columnName: 'Transfer Out Count', selected: true},
  transferOutWeight: {columnName: 'Transfer Out Weight', selected: true},
  transferOutBiomass: {columnName: 'Transfer Out Biomass', selected: true},
  fedAmount: {columnName: 'Feed Amount', selected: true},
  biomassGrowth: {columnName: 'Net Growth', selected: true},
};

const useFields = () => {
  return {fileFields, defaultColumns: ['Date', 'Site Name', 'Site Number', 'Cage Name']};
};

const TimeseriesDownloadModal: FC<ModalDialogProps<any, any>> = ({dialogProps}) => {
  const {startDate: globalStartDate, endDate: globalEndDate} = useGetLongTermRange();
  const [startDate, setStartDate] = useState<Date | null>();
  const [endDate, setEndDate] = useState<Date | null>();
  const {planId} = useParseURL();

  const [disable, setDisable] = useState<boolean>(false);
  const {data: plan} = useGetPlan({resourceId: planId});
  const [csvFile, setCSVFile] = useState<boolean>(true);
  const {t} = useTranslation('common');

  const {fileFields, defaultColumns} = useFields();

  const [selectedFields, setSelectedFields] = useState(fileFields);

  useEffect(() => {
    if (globalStartDate && !startDate) {
      setStartDate(globalStartDate);
    }
    if (globalEndDate && !endDate) {
      setEndDate(globalEndDate);
    }
  }, [globalStartDate, globalEndDate]);

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const selectionState = {
      ...selectedFields,
      [event.target.name]: {...selectedFields[event.target.name], selected: event.target.checked},
    };
    setSelectedFields(selectionState);
  };

  const handleSwitchChange = (event: boolean) => {
    const newSelection: any = {};
    Object.keys(selectedFields).forEach((field) => {
      newSelection[field] = {...selectedFields[field], selected: event};
    });
    setSelectedFields(newSelection);
  };

  const handleClose = (event: any) => {
    dialogProps.onClose && dialogProps.onClose(event, 'escapeKeyDown');
  };

  return (
    <GenericDialog {...dialogProps} scroll="paper">
      <div style={{width: '100%'}}>
        <Box display="flex">
          <Box display="flex" m={1} p={1} flexGrow={1}>
            <Typography variant="h6"> {t('downloadTimeSeriesReport')}</Typography>
          </Box>
          <Divider orientation="vertical" flexItem />
          <Box m={1}>
            <IconButton aria-label="close" onClick={handleClose} size="large">
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>
      </div>
      <Divider />
      <DialogContent>
        <CheckBoxGrid spacing={2}>
          <FormGroup style={{maxHeight: 400, minWidth: 600}}>
            {Object.keys(selectedFields).map((field) => (
              <FormControlLabel
                key={field}
                control={<Checkbox checked={selectedFields[field].selected} onChange={handleChange} name={field} />}
                label={t(field)}
              />
            ))}
          </FormGroup>
        </CheckBoxGrid>
        <Button
          onClick={() => {
            handleSwitchChange(true);
          }}
        >
          {t('selectAll')}
        </Button>
        <Button
          onClick={() => {
            handleSwitchChange(false);
          }}
        >
          {t('clear')}
        </Button>
        <Box display="flex" margin={1}>
          <Grid container spacing={2}>
            <Grid size={{xs: 6}}>
              <InputMenuDatePicker
                value={startDate}
                onChange={(date: Date | null) => {
                  setStartDate(date);
                }}
                minDate={plan?.startDate ? dateReader(plan?.startDate) : undefined}
                maxDate={globalEndDate}
              />
            </Grid>
            <Grid size={{xs: 6}}>
              <InputMenuDatePicker
                value={endDate}
                onChange={(date: Date | null) => {
                  setEndDate(date);
                }}
                minDate={startDate ?? undefined}
                maxDate={globalEndDate}
              />
            </Grid>
          </Grid>
        </Box>
        <Stack direction="row" alignItems="flex-end">
          <FormControl sx={{pl: 1, pt: 1, flexGrow: 1}}>
            <FormLabel component="legend">{t('fileFormat')}</FormLabel>
            <RadioGroup row>
              <FormControlLabel
                control={
                  <Radio
                    checked={csvFile}
                    onChange={() => {
                      setCSVFile(true);
                    }}
                    name={'csv'}
                  />
                }
                label={'csv'}
              />
              <FormControlLabel
                control={
                  <Radio
                    checked={!csvFile}
                    onChange={() => {
                      setCSVFile(false);
                    }}
                    name={'excel'}
                  />
                }
                label={'excel'}
              />
            </RadioGroup>
          </FormControl>
          <Button
            variant="contained"
            sx={{mr: 1}}
            onClick={(event) => {
              const columns = [...defaultColumns];
              Object.values(selectedFields).map((field) => field.selected && columns.push(field.columnName));
              plan &&
                startDate &&
                endDate &&
                getTimeSeriesCSV({
                  plan,
                  suffix: t('downloadTimeSeriesFileNameSuffix'),
                  startDate,
                  endDate,
                  columns,
                  setDisable,
                  csv: csvFile,
                })
                  .catch((error) => {
                    Alert.error(error);
                  })
                  .then(() => {
                    handleClose(event);
                  });
            }}
            endIcon={<GetApp />}
            loading={disable}
            loadingPosition="end"
            data-testid="timeseries-download"
          >
            {t('download')}
          </Button>
        </Stack>
      </DialogContent>
    </GenericDialog>
  );
};
export default TimeseriesDownloadModal;

const CheckBoxGrid = styled(Grid)(({theme}) => ({
  padding: theme.spacing(1),
  border: '1px solid',
  borderRadius: theme.spacing(1),
  borderColor: theme.palette.divider,
  backgroundColor: theme.palette.background.default,
}));

const EXCEL_EXTENSION = '.xlsx';
const CSV_EXTENSION = '.csv';
const BASE_URL = getBaseUrl('');

const getTimeSeriesCSV = async ({
  plan,
  suffix,
  columns,
  startDate,
  endDate,
  setDisable,
  csv,
}: {
  plan: Plan;
  suffix: string;
  columns: string[];
  startDate: Date;
  endDate: Date;
  setDisable: Dispatch<SetStateAction<boolean>>;
  csv: boolean;
}): Promise<void> => {
  const _method = 'GET';
  setDisable(true);

  let request: Promise<AxiosResponse<string>>;
  if (csv) {
    request = backendCommunicationInstance.getPlanTimeseriesFile(plan.id, columns, startDate, endDate);
  } else {
    request = axios.request({
      url: `${BASE_URL}/plans/${plan.id}/timeseries?columns=${columns
        .join('&columns=')
        .replaceAll(' ', '+')}&startDate=${localDateSender(startDate)}&endDate=${localDateSender(endDate)}`,
      method: _method,
      responseType: 'blob',
    });
  }
  const extension = csv ? CSV_EXTENSION : EXCEL_EXTENSION;

  await request.then(({data}) => {
    FileDownload(data, `${plan.name.replace(/\s+/g, '_')}_${suffix}${extension}`);
  });
};
